import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { dateComplete } from '../dossier/dates';
import { messageDErreur, useSession } from '../auth/session';
import { Alerte, Bouton, Carte } from '../ui';
import type { SuppressionDossier } from '../dossier/types';

/**
 * Validation de la suppression d'un dossier, depuis le lien reçu par e-mail.
 *
 * **Cet écran exige une session**, contrairement à celui de l'invitation. La différence est
 * volontaire : le parent invité n'a pas encore de compte et lui en demander un serait le
 * perdre, alors que le parent à qui l'on demande de valider une destruction en a forcément
 * un — c'est le sien. Exiger les deux preuves, le lien *et* la session, évite qu'un message
 * transféré suffise à détruire un dossier.
 *
 * Le texte est en français, comme celui de `Rejoindre` : les écrans atteints par un lien
 * d'e-mail suivent ce précédent du dépôt. Le message, lui, part bien dans la langue du
 * destinataire — c'est lui qui arrive sans prévenir dans une boîte aux lettres.
 *
 * Refuser est présenté sur le même plan que valider, et non comme une sortie de secours :
 * une demande de suppression est une question, pas une notification à acquitter.
 */
export function SupprimerDossier({
  jeton,
  surDossier,
}: {
  jeton: string;
  surDossier?: (id: string) => void;
}) {
  const { client } = useSession();
  const queryClient = useQueryClient();
  const [refuse, setRefuse] = useState(false);
  const [archive, setArchive] = useState<SuppressionDossier | null>(null);

  const rafraichir = () => {
    void queryClient.invalidateQueries({ queryKey: ['case-files'] });
    void queryClient.invalidateQueries({ queryKey: ['case-file'] });
  };

  const valider = useMutation({
    mutationFn: () =>
      client.request<SuppressionDossier>('/case-files/deletions/confirm', {
        method: 'POST',
        body: { token: jeton },
      }),
    onSuccess: (resultat) => {
      setArchive(resultat);
      rafraichir();
    },
  });

  const refuser = useMutation({
    mutationFn: () =>
      client.request('/case-files/deletions/decline', { method: 'POST', body: { token: jeton } }),
    onSuccess: () => {
      setRefuse(true);
      rafraichir();
    },
  });

  if (refuse) {
    return (
      <>
        <h1>C’est noté</h1>
        <Carte>
          <p style={{ margin: 0 }}>
            Le dossier n’est pas supprimé, et rien n’a changé. Votre réponse a été
            enregistrée ; personne ne vous relancera.
          </p>
        </Carte>
      </>
    );
  }

  if (archive) {
    const purge = archive.purgeAt ? new Date(archive.purgeAt) : null;
    return (
      <>
        <h1>La suppression est validée</h1>
        <Carte>
          <p style={{ marginTop: 0 }}>
            {purge ? (
              <>
                Le dossier sera détruit définitivement le <strong>{dateComplete(purge)}</strong>.
              </>
            ) : (
              <>Le dossier est archivé.</>
            )}
          </p>
          <p>
            D’ici là, il reste consultable — vous pouvez le relire et en demander un export —
            mais plus personne ne peut y écrire. La suppression peut être annulée à tout
            moment, par l’un comme par l’autre, depuis le dossier lui-même.
          </p>
          {surDossier ? (
            <Bouton variante="secondaire" onClick={() => surDossier(archive.caseFileId)}>
              Revenir au dossier
            </Bouton>
          ) : null}
        </Carte>
      </>
    );
  }

  return (
    <>
      <h1>Supprimer un dossier</h1>
      <Carte>
        <p style={{ marginTop: 0 }}>
          La suppression d’un dossier Repère attend votre validation. <strong>Rien n’est
          supprimé pour l’instant.</strong>
        </p>
        <p>
          Si vous la validez, le dossier reste consultable trente jours sans pouvoir être
          modifié, puis il est détruit définitivement. Pendant ce délai, la suppression peut
          être annulée à tout moment, par l’un comme par l’autre.
        </p>
        <p className="discret">
          Vous pouvez aussi refuser : le dossier continuera exactement comme avant, et
          personne ne sera relancé.
        </p>

        {valider.isError ? (
          <Alerte titre="La validation n’a pas pu être enregistrée">
            {messageDErreur(valider.error)}
          </Alerte>
        ) : null}
        {refuser.isError ? (
          <Alerte titre="La réponse n’a pas pu être enregistrée">
            {messageDErreur(refuser.error)}
          </Alerte>
        ) : null}

        <Bouton onClick={() => valider.mutate()} disabled={valider.isPending || refuser.isPending}>
          {valider.isPending ? 'En cours…' : 'Valider la suppression'}
        </Bouton>{' '}
        <Bouton
          variante="secondaire"
          onClick={() => refuser.mutate()}
          disabled={valider.isPending || refuser.isPending}
        >
          Ne pas supprimer
        </Bouton>
      </Carte>
    </>
  );
}
